import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { get } from "lodash";
import mediator from "@tvg/mediator";
import { getAuthTokenCookie } from "@tvg/sh-utils/sessionUtils";
import { Button } from "@tvg/design-system/web";
// @ts-ignore
import Context from "@fdr-mar/promos-context";
// @ts-ignore
import pathUtils from "@fdr-mar/utils/pathHandlingUtils";
// @ts-ignore
import gtmUtils from "@fdr-mar/utils/gtmEventHandlers";
import { isXSell, openExternalLink } from "@tvg/sh-utils/mobileUtils";
import { isFdrDesk } from "@tvg/utils/generalUtils";
import { getSession } from "@urp/unified-modules/src/utils/account";
import { openUserHomepage } from "@urp/unified-modules/src/utils/account/openUserHomepage";
import { onbackPromosModal } from "@urp/promos-page/redux/actions";

import tvgConf from "@tvg/conf";
import { PromosContent, LinkProps } from "../../types";

import { OptinButtonContainer } from "./styled-components";

const { PromoContext, OptinContext, PathContext } = Context;

export interface OptinButtonProps {
  content: PromosContent;
  isStaticPromo?: boolean;
}

interface OptinContextType {
  handleOptinClick: () => void;
  isOpted: boolean;
  error: Error;
}

const OptinButton = ({ content, isStaticPromo = false }: OptinButtonProps) => {
  const promoContext = useContext(PromoContext) as {
    promoType: string;
    date: Date;
  };
  const optinContext = useContext<OptinContextType>(OptinContext);
  const pathContext = useContext<typeof PathContext>(PathContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    linkProps: LinkProps,
    handleOptinClick: Function
  ) => {
    const { linkLabel } = content;
    let urlLink;
    let pageOrigin = "promo offer page";

    if (linkProps.tag === "link") {
      const linkPath = linkProps.path;
      let pathName = get(linkPath, "pathname", "/");
      const search = get(linkPath, "search", "");
      const hash = get(linkPath, "hash", "");

      if (pathName === "/account" && isFdrDesk) {
        event.preventDefault();
        event.stopPropagation();
        if (!getAuthTokenCookie()) {
          getSession("ForceNew");
        }
      }

      if (pathName === "/registration" || pathName === "/signup") {
        pathName = "/registration";
        mediator.base.dispatch({
          type: "CLOSE_PROMOS_MODAL"
        });
      }

      if (hash === "#promos" && search !== "") {
        pageOrigin = "promo hub landing";
      }
      urlLink = `${pathName}${search}${hash}`;
    } else {
      urlLink = get(linkProps, "url", "");
    }
    gtmUtils.handleSiteClickDataLayerUpdate({ linkLabel, urlLink });
    gtmUtils.handleNavigationDataLayerUpdate(linkLabel, pageOrigin, urlLink);

    if (urlLink === "/account" && isXSell()) {
      event.preventDefault();
      event.stopPropagation();
      return openUserHomepage();
    }

    handleOptinClick(urlLink, content.path?.cached_url);

    return undefined;
  };
  const renderButtonLink = (handleOptinClick: () => void) => {
    const path = content.bloks?.length ? content.bloks[0]?.path : content.path;
    const linkType = path?.linktype;
    const linkLabel: string = content.bloks?.length
      ? (content.bloks[0]?.linkLabel as string)
      : content.linkLabel;

    let buttonLinkProps: LinkProps;

    const promoContextValue =
      pathContext.currentSlug !== "promos"
        ? promoContext
        : {
            promoType: get(content, "segment", ""),
            promoCode: get(content, "promoCode", "")
          };

    const isHTTP = path?.url?.startsWith("http") || false;

    const { device } = tvgConf();
    const domain = tvgConf().config(`domain.${device}`);
    const isInternalPath = (url?: string) =>
      url &&
      (url.indexOf(domain) > -1 ||
        /fdbox|(?<!sportsbook\.)fanduel(\.|-)|racing/gi.test(url));

    if (linkType === "story" || (!isHTTP && linkType === "url")) {
      buttonLinkProps = pathUtils?.setInternalPath(path);
    } else {
      buttonLinkProps = pathUtils?.setExternalPath(path, promoContextValue);
    }

    return (
      <Button
        variant={optinContext.isOpted ? "primary" : "promo"}
        isDisabled={!!optinContext.error || promoContext.date}
        onClick={(event) => {
          const promoPath =
            buttonLinkProps.tag === "link"
              ? buttonLinkProps.path
              : buttonLinkProps.url;
          if (
            isStaticPromo &&
            !isInternalPath(buttonLinkProps?.url) &&
            buttonLinkProps.tag === "a" &&
            isHTTP
          ) {
            openExternalLink(promoPath);
          } else if (optinContext.isOpted) {
            if (isHTTP) {
              openExternalLink(promoPath as string);
            } else {
              dispatch(onbackPromosModal(false));
              navigate(promoPath);
            }
          } else {
            handleClick(event, buttonLinkProps, handleOptinClick);
          }
        }}
        qaLabel="optinButtonLink"
      >
        {linkLabel}
      </Button>
    );
  };

  const renderButton = () => {
    const ctaLabel = get(content, "ctaLabel", "");

    return (
      <Button
        variant="promo"
        onClick={() => optinContext.handleOptinClick()}
        qaLabel="optinButton"
        isDisabled={!!optinContext.error || promoContext.date}
      >
        {ctaLabel}
      </Button>
    );
  };

  const verifyOptinStatus = (handleOptinClick: () => void) => (
    <>
      {optinContext.isOpted
        ? renderButtonLink(handleOptinClick)
        : renderButton()}
    </>
  );

  const { forOptin, isForBanner } = content;
  const className = isForBanner ? "promosBannerBtn" : "promosOptInButton";

  return (
    <OptinButtonContainer className={className}>
      {forOptin
        ? verifyOptinStatus(optinContext.handleOptinClick)
        : renderButtonLink(optinContext.handleOptinClick)}
    </OptinButtonContainer>
  );
};

export default OptinButton;
